import posthog from 'posthog-js'
import { useAuthStore } from '../stores/auth'
import { resolveFrontendAnalyticsEnvironment } from '../utils/analyticsEnvironment'
import { logger } from '../utils/logger'
import { getRouteTemplate } from '../utils/routeTemplate'

type UserType = 'therapist' | 'child' | 'anonymous'

interface CaptureEvent {
  name: string
  properties?: Record<string, unknown>
}

export interface CaptureContext {
  page?: {
    path: string
    title: string
    referrer?: string
  }
  feature?: {
    name: string
    category?: string
  }
  session?: {
    duration_ms?: number
    time_since_last_event_ms?: number
  }
  device?: {
    screen_width?: number
    screen_height?: number
  }
}

class PostHogService {
  private initialized = false
  private consentGiven = false
  private userType: UserType = 'anonymous'
  private lastEventTime: number = Date.now()
  private sessionStartTime: number = Date.now()

  public init(): void {
    if (this.initialized) {
      return
    }

    const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
    const apiHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com'

    if (!apiKey) {
      if (import.meta.env.DEV) {
        logger.warn('[PostHog] Disabled: VITE_POSTHOG_KEY missing')
      } else {
        logger.error('[PostHog] Failed to init: API key missing in production')
      }
      return
    }

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        autocapture: false,
        capture_pageview: false,
        capture_performance: false,
        disable_surveys: false,
        mask_all_text: true,
        mask_all_element_attributes: true,
        persistence: 'localStorage',
        // Session recording will be started conditionally for therapists only
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
            email: true,
            text: true,
            textarea: true,
            tel: true,
            url: true,
            number: true,
          },
          // Block any elements with this class or data attribute
          blockSelector: '.ph-no-capture,[data-ph-no-capture="true"]',
        },
      })

      posthog.register({
        environment: resolveFrontendAnalyticsEnvironment(),
      })

      this.initialized = true
      this.sessionStartTime = Date.now()
      this.lastEventTime = Date.now()
    } catch (error) {
      logger.error('[PostHog] Initialization error:', error)
    }
  }

  public setConsent(given: boolean): void {
    this.consentGiven = given

    if (!given) {
      // Never initialize PostHog just to opt it out: init() itself makes a
      // network request. If it was never started, there is nothing to stop.
      if (this.initialized) {
        posthog.opt_out_capturing()
        posthog.stopSessionRecording?.()
      }
      return
    }

    if (!this.initialized) {
      this.init()
    }

    if (!this.initialized) {
      logger.warn('⚠ [PostHog] Still not initialized after init() call')
      return
    }

    posthog.opt_in_capturing()
    this.configureIdentity()
  }

  private async hashString(value: string): Promise<string> {
    const data = new TextEncoder().encode(value)
    const digest = await crypto.subtle.digest('SHA-256', data)
    const bytes = Array.from(new Uint8Array(digest))
    return bytes.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  private async configureIdentity(): Promise<void> {
    const { currentTherapist } = useAuthStore.getState()

    if (currentTherapist) {
      this.userType = 'therapist'
      // Hash therapist id before sending
      const distinctId = await this.hashString(`therapist:${currentTherapist.id}`)
      posthog.identify(distinctId, {
        role: 'therapist',
        locale: navigator.language,
        theme: 'therapist',
      })
      posthog.reloadFeatureFlags?.()
      // Start session recording for therapists only with sampling ~0.3
      if (Math.random() < 0.3) {
        posthog.startSessionRecording?.()
      } else {
        posthog.stopSessionRecording?.()
      }
    } else {
      // Anonymous or child flows
      const anonId = posthog.get_distinct_id?.() || undefined
      posthog.reset()
      if (anonId) {
        posthog.identify(anonId, { role: 'anonymous' })
      }
      this.userType = 'anonymous'
      posthog.stopSessionRecording?.()
    }
  }

  public setUserType(userType: UserType): void {
    this.userType = userType
    if (!this.initialized || !this.consentGiven) return
    if (userType === 'therapist') {
      posthog.startSessionRecording?.()
    } else {
      posthog.stopSessionRecording?.()
    }
  }

  public capture(event: CaptureEvent): void {
    this.captureWithContext(event.name, event.properties)
  }

  public captureWithContext(
    name: string,
    properties?: Record<string, unknown>,
    context?: CaptureContext
  ): void {
    if (!this.initialized || !this.consentGiven) {
      return
    }

    const now = Date.now()
    const timeSinceLastEvent = now - this.lastEventTime
    this.lastEventTime = now
    const sessionDuration = now - this.sessionStartTime

    const routeTemplate = context?.page?.path || getRouteTemplate(window.location.pathname)
    const enrichedProperties = {
      ...properties,
      // Page context - route template only, never the concrete path/ids.
      $current_url: `${window.location.origin}${routeTemplate}`,
      $pathname: routeTemplate,
      page_title: context?.page?.title || document.title,
      referrer: context?.page?.referrer ?? '',

      // Feature context
      feature_name: context?.feature?.name,
      feature_category: context?.feature?.category,

      // Session context
      session_duration_ms: context?.session?.duration_ms || sessionDuration,
      time_since_last_event_ms: context?.session?.time_since_last_event_ms || timeSinceLastEvent,

      // Device context
      screen_width: context?.device?.screen_width || window.screen.width,
      screen_height: context?.device?.screen_height || window.screen.height,

      // Standard properties
      user_type: this.userType,
      source: properties?.source ?? 'frontend',
      route_template: properties?.route_template ?? routeTemplate,
      environment: properties?.environment ?? resolveFrontendAnalyticsEnvironment(),
      app_version: (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev',
    }

    try {
      posthog.capture(name, enrichedProperties)
      if (import.meta.env.DEV) {
        logger.debug(`[PostHog] ${name}`)
      }
    } catch (error) {
      logger.error(`[PostHog] Error capturing event '${name}':`, error)
    }
  }

  public pageview(title: string, path: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }
    if (!this.consentGiven) {
      return
    }

    // path is already a route template (see analyticsService.trackPageView).
    posthog.capture('$pageview', {
      $current_url: `${window.location.origin}${path}`,
      $title: title,
      $pathname: path,
      ...properties,
      user_type: this.userType,
      source: properties?.source ?? 'frontend',
      route_template: properties?.route_template ?? path,
      environment: properties?.environment ?? resolveFrontendAnalyticsEnvironment(),
    })
  }

  public reset(): void {
    if (!this.initialized) return
    posthog.reset()
    this.userType = 'anonymous'
    posthog.stopSessionRecording?.()
  }

  public async identifyTherapist(therapist: { id: string }): Promise<void> {
    if (!this.initialized || !this.consentGiven) return
    
    this.userType = 'therapist'
    // Hash therapist id before sending
    const distinctId = await this.hashString(`therapist:${therapist.id}`)
    posthog.identify(distinctId, {
      role: 'therapist',
      locale: navigator.language,
      theme: 'therapist',
    })
    posthog.reloadFeatureFlags?.()
    // Start session recording for therapists only with sampling ~0.3
    if (Math.random() < 0.3) {
      posthog.startSessionRecording?.()
    } else {
      posthog.stopSessionRecording?.()
    }
  }
}

export const posthogService = new PostHogService()
